import { computed, inject, type InjectionKey } from 'vue'
import { useRouter } from 'vue-router'
import { openSupportSessionTab } from '@gat-ship/support-session'
import { sortSupportSessionsByGrantedToAsc } from '../model/support-session-utils'
import { useSupportApiCalls } from './useSupportApiCalls'
import { useSupportSessionDataSource } from './useSupportSessionDataSource'

/**
 * Business logic for the Gatship (support) user's side of support
 * sessions: the sessions assigned to them, starting an assigned session
 * (opening the dedicated support-session tab) and ending one. Created and
 * provided exactly once, at app root, by `SupportSessionAssignedProvider`
 * — and only for Gatship support users. Consumers must inject the shared
 * instance with `useSupportSessionAssignedContext()`, never create their
 * own. The granting user's side (creating a session, own/history lists)
 * lives in `useSupportSession`.
 */
export function useSupportSessionAssigned() {
  const router = useRouter()
  const { endSupportSessionRequest } = useSupportApiCalls()

  const assignedSessionsDataSource = useSupportSessionDataSource('assigned')

  /** Opens the assigned support session in its dedicated tab. */
  function startSupportSession(sessionId: string) {
    const rootHref = router.resolve('/').href
    openSupportSessionTab(sessionId, rootHref)
  }

  /** Ends an assigned support session and drops it from the list. */
  async function endSupportSession(sessionId: string, commandId: string) {
    const result = await endSupportSessionRequest(sessionId, commandId)
    assignedSessionsDataSource.removeSupportSession(sessionId)
    return result
  }

  const commands = {
    startSupportSession,
    endSupportSession
  }

  /** Number of active sessions assigned to the user (drives the sidebar badge). */
  const assignedSessionsCount = computed(
    () => assignedSessionsDataSource.data.value.length
  )

  /** Assigned sessions sorted by `grantedTo`, soonest first (drives the list). */
  const sortedAssignedSessions = computed(() =>
    sortSupportSessionsByGrantedToAsc(assignedSessionsDataSource.data.value)
  )

  return {
    assignedSessionsDataSource,
    assignedSessionsCount,
    sortedAssignedSessions,
    commands
  }
}

export const supportSessionAssignedKey = Symbol(
  'support-session-assigned'
) as InjectionKey<ReturnType<typeof useSupportSessionAssigned>>

/**
 * Inject the shared service instance provided at app root by
 * `SupportSessionAssignedProvider`. Returns `undefined` for non-Gatship
 * users (the provider doesn't provide the key for them), so components
 * that also render on end-user surfaces must guard their usage. The
 * explicit `undefined` default keeps Vue's missing-injection dev warning
 * quiet on those surfaces.
 */
export function useSupportSessionAssignedContext() {
  return inject(supportSessionAssignedKey, undefined)
}
